import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/frontend/lib/utils'

// --- FormDescription ---

interface FormDescriptionProps extends React.ComponentProps<'p'> {}

export function FormDescription({ className, ...props }: FormDescriptionProps) {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />
}

// --- FormError ---

type ErrorValue = string | { message: string } | undefined

interface FormErrorProps extends React.ComponentProps<'p'> {
  errors?: ErrorValue[]
}

function formatError(error: ErrorValue): string | null {
  if (!error) return null
  if (typeof error === 'string') return error
  return error.message
}

export function FormError({ errors, className, ...props }: FormErrorProps) {
  const firstError = errors?.find((e) => e != null)
  const message = formatError(firstError)

  if (!message) return null

  return (
    <p className={cn('text-xs font-medium text-destructive-fg', className)} {...props}>
      {message}
    </p>
  )
}

// --- FormLabel ---

const formLabelVariants = cva('font-semibold leading-none', {
  variants: {
    variant: {
      default: 'text-foreground',
      muted: 'text-muted-foreground',
    },
    size: {
      default: 'text-sm',
      lg: 'text-base',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
})

export type FormLabelVariants = VariantProps<typeof formLabelVariants>

interface FormLabelProps extends React.ComponentProps<'label'>, FormLabelVariants {
  disabled?: boolean
  error?: boolean
  required?: boolean
}

export function FormLabel({
  className,
  variant,
  size,
  disabled,
  error,
  children,
  htmlFor,
  required,
  ...props
}: FormLabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        formLabelVariants({ variant, size }),
        disabled && 'text-muted-foreground cursor-not-allowed',
        error && 'text-destructive-fg',
        className
      )}
      {...props}
    >
      {children}
      {required && <span className="text-destructive-fg"> *</span>}
    </label>
  )
}
